import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    IconButton,
    alpha,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGridPremium, GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import { debounce } from 'lodash';
import {
    getContracts,
    GetContractsParams,
    archiveContract,
    duplicateContract,
} from '../contracts.api';
import {
    Fp1Contract,
    CONTRACT_STATUS_LABELS,
    CONTRACT_STATUS_COLORS,
    CONTRACT_DISCOUNT_TYPE_LABELS,
    ALL_CONTRACT_STATUSES,
    ALL_DISCOUNT_TYPES,
} from '../contracts.types';
import Fp1ContractDetailDialog from './Fp1ContractDetailDialog';
import Fp1ContractFormDialog from './Fp1ContractFormDialog';

// ── Styled ─────────────────────────────────────────────────────────────────

const FilterBar = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
}));

// ── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '--';

const DEFAULT_PARAMS: GetContractsParams = {
    page: 1,
    per_page: 25,
    sort_by: 'updated_at',
    sort_dir: 'desc',
};

// ── Component ──────────────────────────────────────────────────────────────

export default function Fp1ContractsTab() {
    const theme = useTheme();

    const [contracts, setContracts] = useState<Fp1Contract[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [snackMessage, setSnackMessage] = useState<string | null>(null);

    const [params, setParams] = useState<GetContractsParams>(DEFAULT_PARAMS);
    const [searchDraft, setSearchDraft] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [discountTypeFilter, setDiscountTypeFilter] = useState('');

    const [selectedContract, setSelectedContract] = useState<Fp1Contract | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    const fetchContracts = useCallback((p: GetContractsParams) => {
        setLoading(true);
        getContracts(p)
            .then((res) => {
                setContracts(res.data.data);
                setTotalRows(res.data.meta.total);
            })
            .catch(() => setSnackMessage('Failed to load contracts'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchContracts(params);
    }, [params, fetchContracts]);

    const debouncedSearch = useRef(
        debounce((val: string) => {
            setParams((p) => ({ ...p, page: 1, search: val || undefined }));
        }, 400),
    ).current;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
        debouncedSearch(e.target.value);
    };

    const handleFilter = (e: React.FormEvent) => {
        e.preventDefault();
        setParams((p) => ({
            ...p,
            page: 1,
            status: statusFilter || undefined,
            discount_type: discountTypeFilter || undefined,
        }));
    };

    const handleClearFilters = () => {
        setSearchDraft('');
        setStatusFilter('');
        setDiscountTypeFilter('');
        setParams(DEFAULT_PARAMS);
    };

    const handlePaginationChange = (model: GridPaginationModel) => {
        setParams((p) => ({
            ...p,
            page: model.page + 1,
            per_page: model.pageSize,
        }));
    };

    const handleRowClick = (contract: Fp1Contract) => {
        setSelectedContract(contract);
        setDetailOpen(true);
    };

    const handleArchive = async (uuid: string) => {
        try {
            await archiveContract(uuid);
            setSnackMessage('Contract archived');
            fetchContracts(params);
        } catch {
            setSnackMessage('Failed to archive contract');
        }
    };

    const handleDuplicate = async (uuid: string) => {
        try {
            await duplicateContract(uuid);
            setSnackMessage('Contract duplicated');
            fetchContracts(params);
        } catch {
            setSnackMessage('Failed to duplicate contract');
        }
    };

    const expiryIndicator = (contract: Fp1Contract) => {
        if (contract.is_expiring_within_30) {
            return (
                <Tooltip title="Expiring within 30 days">
                    <WarningAmberIcon
                        sx={{ fontSize: '1rem', color: theme.palette.error.main, ml: 0.5 }}
                    />
                </Tooltip>
            );
        }
        if (contract.is_expiring_within_60) {
            return (
                <Tooltip title="Expiring within 60 days">
                    <WarningAmberIcon
                        sx={{ fontSize: '1rem', color: theme.palette.warning.main, ml: 0.5 }}
                    />
                </Tooltip>
            );
        }
        return null;
    };

    const columns: GridColDef<Fp1Contract>[] = [
        {
            field: 'contract_name',
            headerName: 'Contract Name',
            flex: 1.5,
            minWidth: 180,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={600}>
                        {row.contract_name}
                    </Typography>
                    {expiryIndicator(row)}
                </Box>
            ),
        },
        {
            field: 'organization_name',
            headerName: 'Organization',
            flex: 1.2,
            minWidth: 150,
            renderCell: ({ row }) => (
                <Typography variant="body2">{row.organization_name ?? '--'}</Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 140,
            renderCell: ({ row }) => (
                <Chip
                    label={CONTRACT_STATUS_LABELS[row.status] ?? row.status}
                    color={CONTRACT_STATUS_COLORS[row.status] ?? 'default'}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                />
            ),
        },
        {
            field: 'start_date',
            headerName: 'Start Date',
            width: 120,
            renderCell: ({ row }) => (
                <Typography variant="body2">{formatDate(row.start_date)}</Typography>
            ),
        },
        {
            field: 'expiration_date',
            headerName: 'Expiration',
            width: 130,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: row.is_expiring_within_30
                                ? theme.palette.error.main
                                : row.is_expiring_within_60
                                ? theme.palette.warning.main
                                : 'text.primary',
                        }}
                    >
                        {formatDate(row.expiration_date)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'discount_type',
            headerName: 'Discount Type',
            width: 170,
            renderCell: ({ row }) => (
                <Typography variant="body2">
                    {row.discount_type
                        ? CONTRACT_DISCOUNT_TYPE_LABELS[row.discount_type] ?? row.discount_type
                        : '--'}
                </Typography>
            ),
        },
        {
            field: 'updated_at',
            headerName: 'Last Updated',
            width: 130,
            renderCell: ({ row }) => (
                <Typography variant="body2">{formatDate(row.updated_at)}</Typography>
            ),
        },
    ];

    const currentPage = (params.page ?? 1) - 1;
    const pageSize = params.per_page ?? 25;
    const lastPage = Math.ceil(totalRows / pageSize);

    return (
        <Box>
            {/* ── Toolbar ─── */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography variant="h6" fontWeight={700}>
                    Contracts
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateOpen(true)}
                >
                    New Contract
                </Button>
            </Box>

            {/* ── Filters ─── */}
            <FilterBar onSubmit={handleFilter}>
                <TextField
                    size="small"
                    label="Search"
                    placeholder="Contract name or org…"
                    value={searchDraft}
                    onChange={handleSearchChange}
                    sx={{ minWidth: 220 }}
                    InputProps={{
                        startAdornment: (
                            <SearchIcon
                                sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }}
                            />
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {ALL_CONTRACT_STATUSES.map((s) => (
                            <MenuItem key={s} value={s}>
                                {CONTRACT_STATUS_LABELS[s]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                        value={discountTypeFilter}
                        label="Discount Type"
                        onChange={(e) => setDiscountTypeFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {ALL_DISCOUNT_TYPES.map((d) => (
                            <MenuItem key={d} value={d}>
                                {CONTRACT_DISCOUNT_TYPE_LABELS[d]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button type="submit" variant="outlined" size="small">
                    Search
                </Button>
                <Button type="button" variant="text" size="small" onClick={handleClearFilters}>
                    Clear
                </Button>
            </FilterBar>

            {/* ── Grid ─── */}
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <DataGridPremium
                    rows={contracts}
                    columns={columns}
                    getRowId={(row) => row.uuid}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={{ page: currentPage, pageSize }}
                    onPaginationModelChange={handlePaginationChange}
                    hideFooter
                    onRowClick={({ row }) => handleRowClick(row)}
                    sx={{
                        border: 0,
                        cursor: 'pointer',
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                        '& .MuiDataGrid-cell': { py: 1 },
                    }}
                    getRowClassName={({ row }) =>
                        row.is_expiring_within_30
                            ? 'expiring-soon-30'
                            : row.is_expiring_within_60
                            ? 'expiring-soon-60'
                            : ''
                    }
                    autoHeight
                />
            </Paper>

            {/* ── Pagination ─── */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {totalRows} total
                </Typography>
                <IconButton
                    size="small"
                    disabled={currentPage === 0 || loading}
                    onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="caption">
                    {currentPage + 1} / {lastPage || 1}
                </Typography>
                <IconButton
                    size="small"
                    disabled={currentPage + 1 >= lastPage || loading}
                    onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            {/* ── Detail dialog ─── */}
            <Fp1ContractDetailDialog
                open={detailOpen}
                contract={selectedContract}
                onClose={() => setDetailOpen(false)}
                onSave={() => {
                    setDetailOpen(false);
                    setSnackMessage('Contract updated');
                    fetchContracts(params);
                }}
                onArchive={(uuid) => {
                    setDetailOpen(false);
                    handleArchive(uuid);
                }}
                onDuplicate={(uuid) => {
                    setDetailOpen(false);
                    handleDuplicate(uuid);
                }}
            />

            {/* ── Create dialog ─── */}
            <Fp1ContractFormDialog
                open={createOpen}
                contract={null}
                onClose={() => setCreateOpen(false)}
                onSave={() => {
                    setCreateOpen(false);
                    setSnackMessage('Contract created');
                    fetchContracts(params);
                }}
            />

            <Snackbar
                open={!!snackMessage}
                autoHideDuration={4000}
                onClose={() => setSnackMessage(null)}
                message={snackMessage}
            />
        </Box>
    );
}
